// ============================================================================
// src/app/navigation/navigationState.ts — hostile-safe navigation-state
// normalization for the B1 navigation facade.
// ============================================================================
// WHY THIS EXISTS
// History state is a persistence boundary: whatever a caller hands to
// navigate(to, {state}) is structured-cloned by the browser and handed back,
// later, to code that did not write it. The naive containment — Object.keys
// plus returning a known-key object by reference — runs Proxy ownKeys traps,
// invokes accessor getters when values are copied, and returns caller-owned
// references so post-call mutation leaks into history. This module is the
// fail-closed replacement:
//
//   * values are snapshotted through own DATA descriptors only
//     (Object.getOwnPropertyDescriptors) — no [[Get]], no iteration protocol,
//     no Object.keys, no spread, no JSON.stringify (toJSON), no coercion;
//   * dangerous keys (__proto__/constructor/prototype) are rejected outright,
//     and every accepted key is written with Object.defineProperty, so no key
//     can mutate the output's [[Prototype]] or invoke a setter;
//   * output is always a FRESH tree — the caller's objects are never returned
//     by reference, at any depth; sparse arrays keep their declared length and
//     exact hole positions;
//   * anything unsupported, hostile, cyclic, or out-of-shape fails the WHOLE
//     state — never a plausible-looking partial downgrade.
//
// THE RESULT CONTRACT (why not bare null)
// normalizeNavigationState returns a result object:
//   {ok: true, value}   — value is the fresh snapshot (null input is the
//                         intentional clear and snapshots to null);
//   {ok: false, reason} — reason is one of a fixed, bounded set of strings.
// A hostile payload is therefore distinguishable from an explicit clear, and
// the caller (appNavigate) REFUSES the navigation with a bounded error
// instead of silently navigating with state that silently became null.
//
// Accepted domain: null, boolean, finite number (-0 allowed; NaN/±Infinity
// rejected), string within the length bound, Array-isArray arrays within the
// length bound, and plain objects ([[Prototype]] Object.prototype or null).
// Everything else — undefined, function, symbol, bigint, class instances
// (Date, Map, Set, RegExp, typed arrays, …) — is unsupported.
//
// PROXY NOTE: proxies cannot be detected in JavaScript. Every reflective step
// is wrapped so a revoked proxy (any trap throws) or a trap reporting an
// accessor-shaped descriptor fails the result. A live proxy whose descriptors
// are data-shaped and in-domain is snapshotted like a plain object: no
// accessor ever runs, and no trap output is returned by reference.
//
// BOUNDS — SHAPE ONLY, NEVER SIZE (pinned by unit tests at exact ±1; they may
// only shrink): depth <= 8 containers, <= 64 own enumerable keys per object
// (named enumerable properties on arrays count; array index slots do not — no
// bound applies to them), strings <= 4096 chars.
//
// There is deliberately NO total-node budget and NO array-length bound. This
// module is a PASS-THROUGH for payload size: it constrains what a state may
// BE, never how much of it there is. A size bound here is a production
// regression, not a safety property — `recordSeq` carries one container plus
// its fields per row, so any bound in the hundreds turns a long list into a
// TypeError on every click (appNavigate REFUSES a failed normalization; see
// the result contract above).
//
// WHY SIZE IS SAFE TO PASS THROUGH — AND WHAT MAKES IT SAFE.
// It is safe ONLY because traversal is ALIAS-PRESERVING. An earlier revision
// removed the node budget while still expanding every repeated reference into a
// fresh copy, and that combination is a defect: it is exponential in depth, not
// linear in input size. A 7-container DAG whose nodes each hold 5 references to
// the next produced 97,656 output containers. Depth and cycle checks do not
// prevent it — the graph is finite, acyclic, and shallow.
//
// `seen` therefore maps each input container to the ONE output container made
// for it, so a repeated reference yields the same output object rather than a
// new subtree. Every input container is visited exactly once: traversal is
// linear in the input, and the output can never be larger than the input.
//
// This also matches the boundary being modelled. history.state is delivered by
// structuredClone, which preserves aliases; per-occurrence expansion was the
// less faithful behaviour as well as the unbounded one. Freshness is unchanged:
// no output object is ever a caller's object, at any depth.
// ============================================================================

// The accepted domain, written as a type. It is closed on purpose: everything
// outside it fails the whole state rather than being downgraded, so widening
// this alias is the same decision as widening the runtime checks below.
export type NavigationStateValue =
  | null
  | boolean
  | number
  | string
  | NavigationStateValue[]
  | {[key: string]: NavigationStateValue};

// The bounded, fixed set of reasons promised by the result contract above.
// Enumerating them here is what makes "bounded" checkable rather than a claim
// in a comment.
export type NavigationStateFailureReason =
  | 'non-finite-number'
  | 'string-length-limit'
  | 'unsupported-value'
  | 'cycle'
  | 'depth-limit'
  | 'nonplain-container'
  | 'hostile-reflection'
  | 'symbol-key'
  | 'accessor'
  | 'dangerous-key'
  | 'key-limit';

export type NavigationStateResult =
  | {readonly ok: true; readonly value: NavigationStateValue}
  | {readonly ok: false; readonly reason: NavigationStateFailureReason};

// The two output container shapes, kept together because `seen` stores them and
// `out` is built as one of them.
type NavigationStateContainer = NavigationStateValue[] | Record<string, NavigationStateValue>;

const MAX_DEPTH = 8;
const MAX_OWN_KEYS = 64;
const MAX_STRING_LENGTH = 4096;

// ECMAScript array indices are the integers 0 .. 2^32 - 2. A property named
// "4294967296" or above is an ORDINARY named property, not an index, and must
// be counted against MAX_OWN_KEYS like any other key.
const MAX_ARRAY_INDEX = 4294967294;
// Array length is bounded by the language, not by policy: `new Array(n)` throws
// RangeError unless n is an integer in [0, 2^32 - 1]. A proxy may report -1,
// 1.5, NaN or 2^32 from its length descriptor, and constructing from that value
// would throw a raw RangeError straight through the {ok, reason} contract.
const MAX_ARRAY_LENGTH = 4294967295;

// Keys that must never cross a persistence boundary by assignment. Even with
// defineProperty below (which cannot invoke the __proto__ setter), these keys
// are rejected outright: a navigation payload has no legitimate use for them.
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const FAIL = (reason: NavigationStateFailureReason): NavigationStateResult =>
  Object.freeze({ok: false as const, reason});

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// One value -> {ok:true, value: fresh copy} | {ok:false, reason}.
//
// `inProgress` is the identity stack of containers currently being snapshotted:
// re-entering one is a cycle. `seen` is the completed input->output map: a
// container that was visited and FINISHED yields its existing output object, so
// the shape of the input graph is preserved and each input container is
// converted exactly once. See the alias note in the header for why that is a
// correctness requirement and not an optimization.
function snapshot(
  value: unknown,
  depth: number,
  inProgress: object[],
  seen: Map<object, NavigationStateContainer>,
): NavigationStateResult {
  if (value === null) return {ok: true, value: null};
  // Each `typeof value` is written out rather than read from one `const type`
  // alias. The tests, the order, and the accepted domain are unchanged: TypeScript
  // narrows a reference through `typeof value === ...` but not through an alias
  // holding its result, so the alias would have forced a cast on every branch.
  if (typeof value === 'boolean') return {ok: true, value};
  if (typeof value === 'number') return Number.isFinite(value) ? {ok: true, value} : FAIL('non-finite-number');
  if (typeof value === 'string')
    return value.length <= MAX_STRING_LENGTH ? {ok: true, value} : FAIL('string-length-limit');
  if (typeof value !== 'object') return FAIL('unsupported-value'); // undefined, function, symbol, bigint

  // ORDER IS LOAD-BEARING. The cycle check must precede the `seen` lookup: a
  // back-edge into a container still being built is a CYCLE and must fail,
  // whereas `seen` would happily resolve it as an alias and accept the state.
  // `inProgress` is the stack (still building); `seen` is the completed map.
  // Array.prototype.includes is an identity search and invokes no proxy trap,
  // so it is safe outside the reflection try-block below.
  if (inProgress.includes(value)) return FAIL('cycle');

  // An already-converted container short-circuits BEFORE the depth check: it is
  // the same node of the input graph, not a deeper one, and re-deriving it would
  // be the exponential path this module exists to avoid.
  // The non-null assertion is discharged by the `has` on the same line: `seen`
  // only ever stores containers, so a hit is never undefined.
  if (seen.has(value)) return {ok: true, value: seen.get(value)!};

  if (depth >= MAX_DEPTH) return FAIL('depth-limit');

  let descriptors: Record<string, PropertyDescriptor>;
  let isArray: boolean;
  try {
    // Array.isArray and getPrototypeOf both throw on a revoked proxy.
    isArray = Array.isArray(value);
    if (!isArray && !isPlainObject(value)) return FAIL('nonplain-container');
    descriptors = Object.getOwnPropertyDescriptors(value);
  } catch {
    return FAIL('hostile-reflection');
  }

  let declaredLength = 0;
  if (isArray) {
    // length is an own, NON-enumerable data property on real arrays, so it is
    // read here from its descriptor — never through [[Get]]. A proxy trap
    // claiming an accessor-shaped, non-number, or non-array-length value is
    // rejected like any other malformed descriptor. This is NOT a size policy:
    // the bound is 2^32 - 1, the largest length the language permits. Without
    // the check, `new Array(-1 | 1.5 | NaN | 2^32)` throws a raw RangeError
    // straight through the {ok, reason} contract.
    const lengthDescriptor = descriptors.length;
    if (!lengthDescriptor || typeof lengthDescriptor.value !== 'number') return FAIL('hostile-reflection');
    const length = lengthDescriptor.value;
    if (!Number.isInteger(length) || length < 0 || length > MAX_ARRAY_LENGTH) return FAIL('hostile-reflection');
    declaredLength = length;
  }

  inProgress.push(value);
  try {
    // new Array(declaredLength) preserves the exact declared length; only
    // present indices are assigned below, so holes stay holes — including
    // trailing holes that ordinary assignment would silently drop.
    const out: NavigationStateContainer = isArray ? new Array<NavigationStateValue>(declaredLength) : {};
    // Recorded BEFORE descending so a repeated reference anywhere below resolves
    // to this same object. A cycle still fails via `inProgress`, which is
    // checked above and is the stack, not the completed map.
    seen.set(value, out);
    let ownEnumerable = 0;
    for (const key of Reflect.ownKeys(descriptors)) {
      if (typeof key === 'symbol') return FAIL('symbol-key');
      const descriptor = descriptors[key];
      if (!descriptor || descriptor.enumerable !== true) continue; // non-enumerable: ignored
      if (typeof descriptor.get === 'function' || typeof descriptor.set === 'function') return FAIL('accessor');
      if (DANGEROUS_KEYS.has(key)) return FAIL('dangerous-key');
      // The own-key bound applies to objects and to NAMED enumerable array
      // properties; array index slots are bounded by the length descriptor.
      // "Index" is the ECMAScript definition — 0 .. 2^32 - 2 — not merely
      // "looks like a non-negative integer". Without the upper bound, keys such
      // as "4294967296" are ordinary named properties that would masquerade as
      // indices and escape MAX_OWN_KEYS entirely.
      const asNumber = Number(key);
      const isArrayIndex =
        isArray &&
        String(asNumber) === key &&
        Number.isInteger(asNumber) &&
        asNumber >= 0 &&
        asNumber <= MAX_ARRAY_INDEX;
      if (!isArrayIndex) {
        ownEnumerable += 1;
        if (ownEnumerable > MAX_OWN_KEYS) return FAIL('key-limit');
      }
      const child = snapshot(descriptor.value, depth + 1, inProgress, seen);
      if (!child.ok) return child;
      // defineProperty, never [[Set]]: no key can reach a setter or the
      // [[Prototype]] of the output.
      Object.defineProperty(out, key, {value: child.value, writable: true, enumerable: true, configurable: true});
    }
    return {ok: true, value: out};
  } finally {
    inProgress.pop();
  }
}

// The facade's entry point. See the result contract in the header: ok:true
// carries a fresh snapshot (null/undefined input is the intentional clear and
// yields value:null); ok:false carries a bounded fixed-string reason, and the
// navigation caller must refuse rather than navigate on a downgraded state.
export function normalizeNavigationState(input: unknown): NavigationStateResult {
  if (input === null || input === undefined) return Object.freeze({ok: true as const, value: null});
  return snapshot(input, 0, [], new Map());
}
